import { match } from "ts-pattern";
import { computed, watch } from "vue";
import type { Router } from "vue-router/auto";
import { isFeatureFlagEnabled } from "../../../shared/lib/posthog";
import { useInstitution } from "../composables/useInstitution";
import { useNavigation } from "../composables/useNavigation";
import { usePermission } from "../composables/usePermission";
import { useAuth } from "../stores/auth";

/**
 * Waits until the user's authentication state becomes "authenticated" or "unauthorized".
 */
async function waitForAuthenticated(
	auth: ReturnType<typeof useAuth>,
): Promise<void> {
	const authState = computed(() => auth.authState);
	return new Promise<void>((resolve) => {
		if (
			authState.value === "authenticated" ||
			authState.value === "unauthorized"
		) {
			resolve();
		}

		const unwatch = watch(authState, async (newState) => {
			if (newState === "authenticated" || newState === "unauthorized") {
				unwatch();
				resolve();
			}
		});
	});
}

const { getUserPermissions } = usePermission();
const { institutionConfig, isEAorIA } = useInstitution();

// Any additional page access checking definition
export const customPermissions = {
	// If institution is pricing_approvals and a school has the Requests page (any non EA/IA school), every user should have access to the Requests page
	hasRequestsPageAccess: () => {
		if (institutionConfig.value.approval_type === "pricing_approvals") {
			return !isEAorIA.value;
		}
	},
	hasReportsPageAccess: () => {
		if (
			institutionConfig.value.region?.toLowerCase() === "gb" ||
			["malaysiamoh", "bibliotechdemoinstitution", "bibliu"].includes(
				institutionConfig.value.institution_id.toLowerCase(),
			)
		) {
			return true;
		}
	},
	hasSupplyPageAccess: () => {
		if (institutionConfig.value.enable_supply_request === "enabled") {
			return true;
		}
	},
};

export function addPermissionsToRouter(
	router: Router,
	{ redirect = "/unauthorized" } = {},
) {
	router.beforeEach(async (to) => {
		const auth = useAuth();

		// Only wait if auth is still loading
		if (auth.authState === "authenticating") {
			await waitForAuthenticated(auth);
		}

		// Handle unauthorized page navigation
		if (to.path === redirect) {
			if (auth.authState === "unauthorized") {
				return;
			}
			// Redirect authenticated users to their home page
			const { getRedirectUrl } = await useNavigation();
			const redirectPath = getRedirectUrl();
			if (redirectPath && redirectPath !== redirect) {
				return redirectPath;
			}
			return;
		}

		// Redirect unauthorized users to unauthorized page
		if (auth.authState === "unauthorized") {
			return redirect;
		}

		const { items } = await useNavigation();
		const navItem = items.filter((item) => item.to === to.path);
		const navGuards = navItem.length > 0 ? navItem?.[0]?.guard : true;
		if (!navGuards) {
			return "/unauthorized";
		}

		// Check permissions for all other routes
		const targetPermission = to.meta.permission;
		const permissions = await getUserPermissions();

		// If custom permission is defined, it will check the custom permission
		const hasCustomPermission = checkCustomPermissions(
			to.meta.customPermission as keyof typeof customPermissions,
		);

		// only redirect to unauthorized page if user doesn't have the permission and custom permission
		if (Array.isArray(targetPermission)) {
			const hasTargetPermission = targetPermission.some(
				(permission) => permissions[permission],
			);
			const hasPermission = checkPermission(
				hasTargetPermission,
				hasCustomPermission,
			);
			if (
				isFeatureFlagEnabled("roles-and-permissions", false) &&
				!hasPermission
			) {
				return redirect;
			}
		}
		if (
			isFeatureFlagEnabled("roles-and-permissions", false) &&
			typeof targetPermission === "string" &&
			!checkPermission(permissions[targetPermission], hasCustomPermission)
		) {
			return redirect;
		}
	});
}

// check if custom permission is defined and return the result
function checkCustomPermissions(
	customPermission: keyof typeof customPermissions,
) {
	if (customPermission && customPermissions[customPermission]) {
		return customPermissions[customPermission]();
	}
	return true;
}

function checkPermission(
	hasTargetPermission: boolean,
	hasCustomPermission: boolean | undefined,
) {
	return match({ hasTargetPermission, hasCustomPermission })
		.with(
			{
				hasCustomPermission: undefined,
			},
			() => hasTargetPermission,
		)
		.with(
			{
				hasTargetPermission: true,
				hasCustomPermission: true,
			},
			() => true,
		)
		.with(
			{
				hasTargetPermission: false,
				hasCustomPermission: true,
			},
			() => true,
		)
		.with(
			{
				hasTargetPermission: true,
				hasCustomPermission: false,
			},
			() => false,
		)
		.with(
			{
				hasTargetPermission: false,
				hasCustomPermission: false,
			},
			() => false,
		)
		.run();
}
